import { Injectable } from '@angular/core';
import { captureException } from '@sentry/angular';

export interface SpeechResult {
  transcript: string;
  timestamp: number;
  lang: string;
  isFinal: boolean;
}
export class SpeechRecognitionConfig {
  /**
   * Returns and sets a collection of SpeechGrammar objects that represent the grammars that will be understood by the current SpeechRecognition.
   * @info https://developer.mozilla.org/en-US/docs/Web/API/SpeechGrammar
   */
  grammars?: unknown;

  /**
   * Returns and sets the language of the current SpeechRecognition.
   * If not specified, this defaults to the HTML lang attribute value, or the user agent's language setting if that isn't set either.
   * @info https://developer.mozilla.org/en-US/docs/Web/API/SpeechRecognition/lang
   */
  lang?: string;

  /**
   * Controls whether continuous results are returned for each recognition, or only a single result.
   * @Default to single (true.)
   */
  continuous = false;

  /**
   * Controls whether interim results should be returned (true) or not (false.)
   * Interim results are results that are not yet final (e.g. the SpeechRecognitionResult.isFinal property is false.)
   */
  interimResults?: boolean;

  /**
   * Sets the maximum number of SpeechRecognitionAlternatives provided per result.
   * @Default value is 1.
   */
  maxAlternatives = 1;

  /**
   * Fired when the user agent has started to capture audio.
   */
  onaudiostart?: (event: Event) => void;

  /**
   * Fired when the user agent has finished capturing audio.
   */
  onaudioend?: (event: Event) => void;

  /**
   * Fired when the speech recognition service has disconnected.
   */
  onend?: (event: Event) => void;

  /**
   * Fired when a speech recognition error occurs.
   */
  onerror?: (event: typeof Event) => void;

  /**
   * Fired when the speech recognition service returns a final result with no significant recognition.
   * This may involve some degree of recognition, which doesn't meet or exceed the confidence threshold.
   */
  onnomatch?: (event: typeof Event) => void;

  /**
   * Fired when the speech recognition service returns a final result
   */
  onresult?: (result: SpeechResult) => void;

  /**
   * Fired when any sound — recognizable speech or not — has been detected.
   */
  onsoundstart?: (event: Event) => void;

  /**
   * Fired when any sound — recognizable speech or not — has stopped being detected. Also available via the onsoundend property.
   */
  onsoundend?: (event: Event) => void;

  /**
   * Fired when sound that is recognized by the speech recognition service as speech has been detected.
   */
  onspeechstart?: (event: Event) => void;

  /**
   * Fired when speech recognized by the speech recognition service has stopped being detected.
   */
  onspeechend?: (event: Event) => void;

  /**
   * Fired when the speech recognition service has begun listening to
   * incoming audio with intent to recognize grammars associated with the current SpeechRecognition.
   */
  onstart?: (event: Event) => void;
}

export const defaultSpeechRecognitionConfig: SpeechRecognitionConfig = {
  continuous: false,
  lang: 'eng-US',
  interimResults: true,
  maxAlternatives: 1,
};

const SpeechRecognition = window['webkitSpeechRecognition'];

@Injectable({
  providedIn: 'root',
})
export class SpeechRecognitionService {
  currentSpeechRecognition: typeof SpeechRecognition;
  currentSpeechRecognitionConfig: SpeechRecognitionConfig | undefined;

  /**
   * on audioEnd event if this flag is set to true then we will restart the speech recognition service again
   * @Usecase when the user go for the long silence period. By default, the speech recognition will be stopped, in this case we start it again.
   */
  private autoRestart = false;

  private speechApiStartTime = 0;

  set autoRestartStatus(val: boolean) {
    this.autoRestart = val;
  }

  initSpeechRecognition(config: SpeechRecognitionConfig = defaultSpeechRecognitionConfig): void {
    try {
      this.currentSpeechRecognitionConfig = config;

      // set SpeechRecognition service properties
      this.speechApiStartTime = new Date().getTime();
      this.currentSpeechRecognition = new SpeechRecognition();
      this.currentSpeechRecognition.continuous = config.continuous;
      this.currentSpeechRecognition.lang = config.lang;
      this.currentSpeechRecognition.interimResults = config.interimResults;
      this.currentSpeechRecognition.maxAlternatives = config.maxAlternatives;

      // set SpeechRecognition service listeners
      this.initSpeechRecognitionEventListeners();
    } catch (error) {
      captureException(error);
    }
  }

  /**
   * Starts the speech recognition service listening to incoming audio with intent to recognize grammars associated with the current SpeechRecognition.
   */
  startSpeechRecognition(config: { autoStart: boolean } = { autoStart: false }): void {
    try {
      this.autoRestart = config.autoStart;
      this.currentSpeechRecognition.start();
    } catch (error) {
      captureException(error);
    }
  }

  /**
   * Stops the speech recognition service from listening to incoming audio, and attempts to return a SpeechRecognitionResult using the audio captured so far.
   */
  stopSpeechRecognition(): void {
    try {
      this.autoRestart = false;
      this.currentSpeechRecognition.stop();
    } catch (error) {
      captureException(error);
    }
  }

  /**
   * Stops the speech recognition service from listening to incoming audio, and doesn't attempt to return a SpeechRecognitionResult.
   */
  abortSpeechRecognition(): void {
    try {
      this.currentSpeechRecognition.abort();
    } catch (error) {
      captureException(error);
    }
  }

  /**
   * Use this when the speech recognition is no more needed.
   */
  dispose(): void {
    this.abortSpeechRecognition();
    this.currentSpeechRecognitionConfig = undefined;
    this.currentSpeechRecognition = null;
  }

  private initSpeechRecognitionEventListeners(): void {
    this.currentSpeechRecognition.onaudiostart = (event: Event) =>
      this.onAudioStartManager(event, this.currentSpeechRecognitionConfig?.onaudiostart);

    this.currentSpeechRecognition.onaudioend = (event: Event) =>
      this.onAudioEndManager(event, this.currentSpeechRecognitionConfig?.onaudioend);

    this.currentSpeechRecognition.onend = (event: Event) =>
      this.onEndManager(event, this.currentSpeechRecognitionConfig?.onend);

    this.currentSpeechRecognition.onerror = (event: typeof Event) =>
      this.onErrorManager(event, this.currentSpeechRecognitionConfig?.onerror);

    this.currentSpeechRecognition.onnomatch = (event: typeof Event) =>
      this.onNoMatchManager(event, this.currentSpeechRecognitionConfig?.onnomatch);

    this.currentSpeechRecognition.onresult = (event: typeof Event) =>
      this.omResultManager(event, this.currentSpeechRecognitionConfig?.onresult);

    this.currentSpeechRecognition.onsoundstart = (event: Event) =>
      this.onSoundStartManager(event, this.currentSpeechRecognitionConfig?.onsoundstart);

    this.currentSpeechRecognition.onsoundend = (event: Event) =>
      this.onSoundEndManager(event, this.currentSpeechRecognitionConfig?.onsoundend);

    this.currentSpeechRecognition.onspeechstart = (event: Event) =>
      this.onSpeechStartManager(event, this.currentSpeechRecognitionConfig?.onspeechstart);

    this.currentSpeechRecognition.onspeechend = (event: Event) =>
      this.onSpeechEndManager(event, this.currentSpeechRecognitionConfig?.onspeechend);

    this.currentSpeechRecognition.onstart = (event: Event) =>
      this.onStartManager(event, this.currentSpeechRecognitionConfig?.onstart);
  }

  private onAudioStartManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onAudioEndManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onEndManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }

    if (this.autoRestart) {
      this.startSpeechRecognition({ autoStart: true });
    }
  }

  private onErrorManager(event: any, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onNoMatchManager(event: typeof Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private omResultManager(event: any, cb?: CallableFunction): void {
    if (cb) {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const speechResult: SpeechResult = {
          isFinal: result.isFinal,
          lang: this.currentSpeechRecognition
            ? this.currentSpeechRecognition.lang
            : defaultSpeechRecognitionConfig.lang,
          timestamp: this.speechApiStartTime + event.timeStamp,
          transcript: transcript,
        };
        cb(speechResult);
      }
    }
  }

  private onSoundStartManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onSoundEndManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onSpeechStartManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onSpeechEndManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }

  private onStartManager(event: Event, cb?: CallableFunction): void {
    if (cb) {
      cb(event);
    }
  }
}
